import { Loader2, ShieldAlert } from "lucide-react";

import { useUsers } from "../api/hooks";
import { Initials, RowActions, StatusBadge } from "./AdminUI";
import { Centered, EmptyRow, ErrorBox, Thead } from "./LoanersTab";

const ROLE_LABEL: Record<string, string> = { investor: "Långiver", loaner: "Låntaker" };

// Coarse "city, country" from the stored geo JSON — informational only (see 0012 migration).
function formatGeo(json: string | null): string | null {
  if (!json) return null;
  try {
    const g = JSON.parse(json) as { city?: string | null; country?: string | null };
    const parts = [g.city, g.country].filter(Boolean);
    return parts.length ? parts.join(", ") : null;
  } catch {
    return null;
  }
}

/** Accounts awaiting a decision — the admin's main queue. Includes accounts flagged for
 *  suspicious login activity (even if active), pending first. */
export function PendingTab() {
  const { data: users, isLoading, isError } = useUsers();

  const queue = (users ?? [])
    .filter((u) => u.status !== "active" || u.requiresReverify)
    .sort((a, b) => (a.status === "pending" && b.status !== "pending" ? -1 : 1));

  if (isLoading) {
    return (
      <Centered>
        <Loader2 className="size-5 animate-spin" /> Laster…
      </Centered>
    );
  }
  if (isError) return <ErrorBox>Kunne ikke laste kontoer.</ErrorBox>;

  return (
    <div className="overflow-x-auto rounded-xl border border-white/10">
      <table className="w-full min-w-[680px] text-left text-sm">
        <Thead cols={["Navn", "E-post", "Rolle", "Opprettet", "Status", "Handling"]} />
        <tbody className="divide-y divide-white/5">
          {queue.map((u) => (
            <tr key={u.id} className="hover:bg-white/[0.03]">
              <td className="px-4 py-3">
                <div className="flex items-center gap-3">
                  <Initials name={u.name} fallback={u.email} />
                  <span className="font-medium text-white">{u.name ?? "—"}</span>
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="text-white/55">{u.email}</div>
                {formatGeo(u.lastLoginGeo) && (
                  <div className="text-xs text-white/35">Sist pålogget fra {formatGeo(u.lastLoginGeo)}</div>
                )}
              </td>
              <td className="px-4 py-3 text-white/55">{ROLE_LABEL[u.role] ?? u.role}</td>
              <td className="px-4 py-3 text-white/45">{u.createdAt}</td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge status={u.status} />
                  {u.requiresReverify && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-red-400/30 bg-red-400/10 px-2 py-0.5 text-xs text-red-300">
                      <ShieldAlert className="size-3.5" /> Sikkerhetsflagg
                    </span>
                  )}
                </div>
              </td>
              <td className="px-4 py-3">
                <RowActions accountId={u.id} status={u.status} />
              </td>
            </tr>
          ))}
          {queue.length === 0 && (
            <EmptyRow cols={6} label="Ingen kontoer venter på godkjenning. 🎉" />
          )}
        </tbody>
      </table>
    </div>
  );
}
